import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, CircleX, RotateCcw, X, Maximize2, Minimize2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReason } from "@/components/aiot/lab/TestPanel";
import { LabCertActions } from "@/components/aiot/lab/LabCert";

const Caption = ({ lab, res, cap }) => {
  const { L, lang } = useLang();
  const reason = useReason(lab);
  const passed = res.scenarios.filter((r) => r.pass).length;
  if (cap.intro) return <><div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5EEAD4]">{L("A RoboSpark AIoT build", "एक RoboSpark AIoT बिल्ड")}</div><div className="mt-1 text-2xl sm:text-4xl font-black">{lang === "hi" ? lab.title_hi : lab.title}</div></>;
  if (cap.outro) return <><div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5EEAD4]">{L("Build complete", "बिल्ड पूरा")}</div><div className="mt-1 text-2xl sm:text-4xl font-black" data-testid="lab-cinema-score">{res.score}/100</div><div className="text-sm text-slate-300">{L(`${passed} of ${res.scenarios.length} scenarios pass`, `${res.scenarios.length} में से ${passed} सीनारियो पास`)}</div></>;
  const { s, r, phase, step } = cap;
  return (
    <>
      <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5EEAD4]">{L("Scenario", "सीनारियो")} {step}/{res.scenarios.length}</div>
      <div className="mt-1 text-lg sm:text-2xl font-extrabold">{lang === "hi" ? s.hi : s.en}</div>
      {phase === 1 && r && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ${r.pass ? "bg-[#059669]" : "bg-[#DC2626]"}`}>
          {r.pass ? <CheckCircle2 className="w-4 h-4" /> : <CircleX className="w-4 h-4" />}{reason(r)}
        </motion.div>
      )}
    </>
  );
};

export const CinemaOverlay = ({ lab, res, cap, playing, onReplay, onExit, full, toggle, cert, snap }) => {
  const { L } = useLang();
  return (
    <div className="absolute inset-0 pointer-events-none z-10" data-testid="lab-cinema">
      <motion.div initial={{ height: 0 }} animate={{ height: "9%" }} className="absolute top-0 inset-x-0 bg-black" />
      <div className="absolute bottom-[9%] inset-x-0 h-[45%] bg-gradient-to-t from-black/75 via-black/35 to-transparent" />
      <motion.div initial={{ height: 0 }} animate={{ height: "9%" }} className="absolute bottom-0 inset-x-0 bg-black" />
      <AnimatePresence mode="wait">
        {cap && (
          <motion.div key={cap.intro ? "i" : cap.outro ? "o" : `${cap.step}-${cap.phase}`} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
            className="absolute left-5 sm:left-8 bottom-[12%] max-w-[80%] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]" data-testid="lab-cinema-caption">
            <Caption lab={lab} res={res} cap={cap} />
          </motion.div>
        )}
      </AnimatePresence>
      {cert && cap?.outro && !playing && <div className="absolute right-5 bottom-[12%] pointer-events-auto" data-testid="lab-cinema-cert"><LabCertActions lab={lab} cert={cert} snap={snap} testid="lab-cinema-cert-actions" /></div>}
      <div className="absolute top-[11%] right-4 flex gap-2 pointer-events-auto">
        {!playing && <button type="button" onClick={onReplay} className="rounded-full bg-white/90 px-3 h-9 text-sm font-bold text-[#0B1F3A] inline-flex items-center gap-1.5" data-testid="lab-cinema-replay"><RotateCcw className="w-4 h-4" />{L("Replay", "फिर से")}</button>}
        <button type="button" onClick={toggle} className="w-9 h-9 rounded-full bg-white/90 grid place-items-center text-[#0B1F3A]" aria-label="Full screen" data-testid="lab-cinema-fullscreen">{full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
        <button type="button" onClick={onExit} className="w-9 h-9 rounded-full bg-white/90 grid place-items-center text-[#0B1F3A]" aria-label="Exit" data-testid="lab-cinema-exit"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};
